import * as React from 'react';
import classnames from 'classnames';

import DropZone, { DropzoneRef } from 'react-dropzone-esm';

import { Binding, Result } from 'core/infrastructure';
import { Convert } from 'core/utils';

import ErrorBlock from './ErrorBlock';
import { FormComponent } from './Form';
import { formatAsElementId } from './Utils';
import { resizeImageBlob, ditherImageBlob, convertImageBlobToJpeg, getImageBlobSize } from './ImageUtils';
import { Res } from 'core/api';

type ResizeMode =
	// Convierte la imagen a un tamaño width * height, ya sea aumentado o reduciendo, y rompiendo aspect ratio si hace falta
	'fixed-size'
	// Si la imagen es mayor que width * height, la reduce hasta que quepa manteniendo el aspect ratio
	| 'shrink-keeping-aspect-ratio';

type TransformImageParams = {
	width: number,
	height: number,
	dither?: boolean,
	resizeMode?: ResizeMode,
	asJpeg?: boolean
};

type ButtonPosition = 'top' | 'right';

interface Props<T> extends React.HTMLProps<ImageSelector<T>> {
	linkTo: Binding.PropertyPath<T>;
	label?: string;
	dropZoneWidth?: number;
	dropZoneHeight?: number;
	dropZoneTheme?: 'light' | 'dark';
	buttonPosition?: ButtonPosition;
	transform?: TransformImageParams;
	canViewFullSize?: boolean;
	onValueChanged?: (image: string) => void;
	defaultEmptyImage?: string;
	boundValue?: string;
	formId?: string;
	readonly?: boolean;
	required?: boolean;
}

interface State {
	fileAsBase64: string;
	errorMsg: string;
}

export class ImageSelector<T> extends React.Component<Props<T>, State> implements FormComponent {

	private dropZone = React.createRef<DropzoneRef>();
	private ref = React.createRef<HTMLDivElement>();

	constructor(props: Props<T>) {

		super(props);

		if (typeof props.boundValue !== 'string')
			throw new Error('Image selector esperaba una imagen en formato base64 string');

		this.state = this.createStateFromProps(props);

		this.onDropImage = this.onDropImage.bind(this);
		this.onOpenImage = this.onOpenImage.bind(this);
		this.onClearImage = this.onClearImage.bind(this);
		this.onExpandImage = this.onExpandImage.bind(this);

		this.getImageSource = this.getImageSource.bind(this);
	}

	createStateFromProps(props: Props<T>): State {

		const image = props.boundValue && props.boundValue.length === 0 ? (props.defaultEmptyImage || '') : props.boundValue;
		return {
			fileAsBase64: image || '',
			errorMsg: ''
		};
	}

	componentDidMount() {
		$('*', this.ref.current!).addClass('needsclick');
	}

	getPath(): string {
		return Binding.propertyPathToString(this.props.linkTo);
	}

	getValue(): Result<string> {
		this.setState({ errorMsg: '' });

		if (this.props.required && this.state.fileAsBase64.length === 0) {
			this.setState({ errorMsg: Res.Msg.MSG_MUST_SELECT_A_VALUE() });
			return Result.error();
		}

		return Result.ok(this.state.fileAsBase64);
	}

	showErrors(errors: string[]): void {
		this.setState({ errorMsg: errors.join(' ') });
	}

	private async getTransformSize(file: Blob): Promise<{ width: number, height: number }> {
		if (!this.props.transform)
			return { width: 60, height: 60 };

		if (this.props.transform.resizeMode === 'fixed-size')
			return { width: this.props.transform.width, height: this.props.transform.height };

		// Si es necesario, reduzco la imagen manteniendo el aspect ratio
		const imageSize = await getImageBlobSize(file);

		// Si la imagen cabe, usamos el tamaño de la imagen directamente
		if (imageSize.width <= this.props.transform.width && imageSize.height <= this.props.transform.height)
			return { width: imageSize.width, height: imageSize.height };

		const hRatio = this.props.transform.width / imageSize.width;
		const vRatio = this.props.transform.height / imageSize.height;
		const ratio = Math.min(hRatio, vRatio);

		return { width: imageSize.width * ratio, height: imageSize.height * ratio };
	}

	private async onDropImage(files: File[]) {

		const { width, height } = await this.getTransformSize(files[0]);

		const applyDither: (file: Blob) => Promise<Blob> = this.props.transform && this.props.transform.dither
			? (file: Blob) => ditherImageBlob(file, width, height)
			: file => Promise.resolve(file);

		const resize: (file: Blob) => Promise<Blob> = this.props.transform
			? (file: Blob) => resizeImageBlob(file, width, height)
			: file => Promise.resolve(file);

		const convertToFileFormat: (file: Blob) => Promise<Blob> = this.props.transform && this.props.transform.asJpeg
			? (file: Blob) => convertImageBlobToJpeg(file, width, height)
			: file => Promise.resolve(file);

		let file = await resize(files[0]);
		file = await applyDither(file);
		file = await convertToFileFormat(file);

		const fileAsBase64 = await Convert.blobToBase64String(file);

		this.setState({ fileAsBase64 }, () => this.notifyValueChanged(fileAsBase64));
	}

	private notifyValueChanged(imageData: string): void {
		this.props.onValueChanged?.(imageData);
	}

	private onOpenImage() {
		this.dropZone.current!.open();
	}

	private onClearImage() {
		const emptyImage = this.props.defaultEmptyImage || '';
		this.setState({ fileAsBase64: emptyImage },
			() => this.notifyValueChanged(emptyImage));
	}

	private onExpandImage() {
		const dataUri = `data:image/*;base64,${this.state.fileAsBase64}`;
		const img = new Image();
		img.src = dataUri;

		const w = window.open();
		if (w) {
			w.document.write(img.outerHTML);
			w.document.close();
		}
	}

	private getImageSource(): string {
		const imageBlob = Convert.base64StringToBlob(this.state.fileAsBase64 || '');
		return imageBlob.size > 0 ? URL.createObjectURL(imageBlob) : '';
	}

	render() {

		const buildButtons = (position: ButtonPosition) => {
			const defaultPos = this.props.buttonPosition ? this.props.buttonPosition : 'right';

			if (defaultPos !== position)
				return null;

			const buttonsClasses = classnames({
				'm-l-sm': position === 'right',
				'float-left': position === 'top'
			});

			return (
				<span className={buttonsClasses}>
					<span className={classnames({ 'm-l-xs': position === 'right' })}>
						<button type='button' className='btn btn-default' onClick={this.onOpenImage}><i className='text-info fa fa-image'></i></button>
					</span>
					<span className='m-l-xs'>
						<button type='button' className='btn btn-default' onClick={this.onClearImage}><i className='text-info fa fa-remove'></i></button>
					</span>
					{this.props.canViewFullSize
						?
						<span className='m-l-xs'>
							<button type='button' className='btn btn-default' onClick={this.onExpandImage}><i className='text-info fa fa-expand'></i></button>
						</span>
						: null}
					{position === 'top' ? <span className='m-b-sm clearfix' /> : null}
				</span>
			);
		};

		const dropZoneClasses = classnames({ 'needsclick': true, 'm-t-sm': this.props.buttonPosition === 'top' });
		const dropZoneTheme = this.props.dropZoneTheme || 'light';
		const dropZoneBorderColor = dropZoneTheme === 'dark' ? '#34495e' : '#e4e5e7';
		const dropZoneBackgroundColor = dropZoneTheme === 'dark' ? '#4c566a' : 'white';

		const dropZoneStyle = {
			width: `${this.props.dropZoneWidth || 64}px`,
			height: `${this.props.dropZoneHeight || 64}px`,
			display: 'inline-block',
			cursor: 'pointer',
			border: `1px solid ${dropZoneBorderColor}`,
			borderRadius: '4px',
			background: dropZoneBackgroundColor
		};

		const source = this.getImageSource();

		const label = this.props.label ? <label className='col-sm-4 control-label'>{this.props.label}</label> : null;

		const imageStyle: React.CSSProperties = {
			borderRadius: '4px',
			maxHeight: '100%',
			maxWidth: '100%',
			height: 'auto',
			width: 'auto',
			border: 'none',
			display: source ? 'block' : 'none'
		};

		return <div ref={this.ref} className={classnames({ 'form-group': true, 'has-error': !!this.state.errorMsg })}>
			{label}
			<div className={this.props.label ? 'col-sm-8' : 'col-sm-12'}>
				{buildButtons('top')}
				<DropZone
					onDrop={this.onDropImage}
					ref={this.dropZone}
					accept={{ 'image/*': ['.png', '.gif', '.jpeg', '.jpg', '.bmp'] }}
					multiple={false}
					noClick={false}
					noKeyboard={true}

				>
					{({ getRootProps, getInputProps }) => (
						<div id={formatAsElementId(this.getPath(), this.props.formId || '')}
							style={dropZoneStyle}
							className={dropZoneClasses}
							{...getRootProps()}>
							{source ? <img className={'needsclick'} src={source} style={imageStyle} /> : null}
							{/* El input es necesario, aunque esté invisible, porque por debajo se come él los eventos para sacar el diálogo de buscar imagen */}
							<input {...getInputProps({})} style={{ display: 'none' }} />
						</div>
					)}
				</DropZone>
				{buildButtons('right')}
			</div>
			<ErrorBlock msg={this.state.errorMsg} />
		</div>;
	}
}
export type ImageSelectorCtor<T> = new (props: Props<T>) => React.Component<Props<T>, State>;
